import {useCallback, useContext, useEffect, useState} from 'react';
import {UNSAFE_NavigationContext as NavigationContext} from 'react-router';

// Interface pour les callbacks de la modale
export interface PromptCallbacks {
  onConfirm: () => void;
  onCancel: () => void;
}

export interface PromptState {
  isOpen: boolean;
  message: string;
  callbacks: PromptCallbacks | null;
}

// État global pour le contrôle de la modale
export const usePromptState = () => {
  const [promptState, setPromptState] = useState<PromptState>({
    isOpen: false,
    message: '',
    callbacks: null,
  });

  const openPrompt = (message: string, callbacks: PromptCallbacks) => {
    setPromptState({
      isOpen: true,
      message,
      callbacks,
    });
  };

  const closePrompt = () => {
    setPromptState((prev) => ({
      ...prev,
      isOpen: false,
    }));
  };

  return {promptState, openPrompt, closePrompt};
};

// Hook pour accéder à l'état global (à implémenter avec Context API dans une application réelle)
let globalPromptState: {
  openPrompt?: (message: string, callbacks: PromptCallbacks) => void;
} = {};

export const setGlobalPromptState = (state: typeof globalPromptState) => {
  globalPromptState = state;
};

function useConfirmExit(confirmExit: () => void, cancelExit: () => void, message: string, when = true) {
  const {navigator} = useContext(NavigationContext);

  useEffect(() => {
    if (!when) {
      return;
    }

    const push = navigator.push;

    navigator.push = (...args: Parameters<typeof push>) => {
      if (globalPromptState.openPrompt) {
        globalPromptState.openPrompt(message, {
          onConfirm: () => {
            push(...args);
            confirmExit();
          },
          onCancel: cancelExit,
        });
      }
      return;
    };

    return () => {
      navigator.push = push;
    };
  }, [navigator, confirmExit, cancelExit, message, when]);
}

export function usePrompt(message: string, when = true, onConfirm?: () => void) {
  useEffect(() => {
    if (when) {
      window.onbeforeunload = function () {
        return message;
      };
    }

    return () => {
      window.onbeforeunload = null;
    };
  }, [message, when]);

  const confirmExit = useCallback(() => {
    if (onConfirm) onConfirm();
  }, [onConfirm]);

  const cancelExit = useCallback(() => {
    // Ne rien faire, seulement annuler la navigation
  }, []);

  useConfirmExit(confirmExit, cancelExit, message, when);
}
